// Collapsed item card on the inquiry items board: what the restaurant
// currently buys (name + spec line + demand numbers) with "open to switch"
// badges for the willing-to-buy flags. Edit expands the card back into the
// wizard; delete is immediate (confirmation handled by the board).

import { Badge, Box, Flex, IconButton, Text } from '@elkaso-app/web-design';
import { RestaurantInquiryItem, VolumePeriod } from 'apis/restaurant-inquiries';
import { describeUnits } from 'pages/inquiry-processor/utils/describe-units';
import { packagingSpecLabel, packagingSpecOf } from 'pages/inquiry-processor/utils/packaging-spec';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

interface ItemCardProps {
  item: RestaurantInquiryItem;
  onEdit: () => void;
  // Undefined hides the control — an app item is declined from the match-maker screen instead
  // of removed here (the restaurant is waiting on a price for it).
  onDelete?: () => void;
  isDeleting: boolean;
}

const price = (v?: number | null) => (v != null ? `AED ${v}` : '—');

// The stored DATEONLY may arrive as a full ISO timestamp — render date-only.
const expiryLabel = (d: string) =>
  new Date(d.slice(0, 10)).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' });

// The wizard never lets ops pick a period today — every item is created as
// PER_ORDER (see wizard-draft.ts) — so "/ month" was always wrong; it's a
// one-off quantity for this inquiry, not a recurring monthly figure. Mapped
// from the real field (rather than hardcoded) so a future period picker
// doesn't silently go back to lying about it.
const VOLUME_PERIOD_LABEL: Record<VolumePeriod, string> = {
  [VolumePeriod.PER_ORDER]: 'per inquiry',
  [VolumePeriod.ONE_TIME]: 'one time',
  [VolumePeriod.WEEKLY]: 'per week',
  [VolumePeriod.MONTHLY]: 'per month',
  [VolumePeriod.QUARTERLY]: 'per quarter',
  [VolumePeriod.ANNUAL]: 'per year',
};

export const ItemCard = ({ item, onEdit, onDelete, isDeleting }: ItemCardProps) => {
  const p = item.masterProduct;
  const specLine = [p?.brand, p?.origin, p?.state, p?.spec].filter(Boolean).join(' · ');
  const unitsLine = describeUnits(p);
  // Badge per switchable attribute; when specific alternatives are stored the
  // badge names them ("Brand: Alyoum, Seara"), otherwise it's open to any.
  const alt = item.acceptableAlternatives ?? {};
  const flexBadge = (label: string, on: boolean, values?: string[]) =>
    on ? (values && values.length ? `${label}: ${values.join(', ')}` : label) : null;
  // Pack-format alternatives are structured, so they render through the shared
  // label helper; the variant's own baseUnit supplies the missing unit word.
  const packagingAlt = (alt.packaging ?? []).map((spec) => packagingSpecLabel(packagingSpecOf(spec), p?.baseUnit));
  const flexible = [
    flexBadge('Brand', item.brandFlexible, alt.brand),
    flexBadge('Origin', item.originFlexible, alt.origin),
    flexBadge('State', item.stateFlexible ?? true, alt.state),
    flexBadge('Variant', item.specFlexible ?? true, alt.spec),
    flexBadge('Packaging', item.packagingFlexible ?? true, packagingAlt),
  ].filter(Boolean) as string[];

  return (
    <Box borderWidth='1px' rounded='md' p='3' bg='white'>
      <Flex justify='space-between' align='flex-start' gap='2'>
        <Box>
          <Text fontWeight='semibold'>{p?.name || `Product #${item.masterProductId}`}</Text>
          <Text fontSize='sm' color='gray.600'>
            {specLine || '—'}
          </Text>
          {unitsLine && (
            <Text fontSize='sm' color='gray.600'>
              {unitsLine}
            </Text>
          )}
        </Box>
        <Flex gap='1'>
          <IconButton aria-label='Edit item' size='xs' variant='ghost' icon={<FiEdit2 />} onClick={onEdit} />
          {onDelete && (
            <IconButton
              aria-label='Remove item'
              size='xs'
              variant='ghost'
              icon={<FiTrash2 />}
              onClick={onDelete}
              isLoading={isDeleting}
            />
          )}
        </Flex>
      </Flex>
      <Flex gap='4' mt='2' fontSize='sm' wrap='wrap'>
        <Text>
          <Box as='span' color='gray.500'>
            Volume:{' '}
          </Box>
          {item.volume} {VOLUME_PERIOD_LABEL[item.volumePeriod ?? VolumePeriod.PER_ORDER]}
        </Text>
        <Text>
          <Box as='span' color='gray.500'>
            Current:{' '}
          </Box>
          {price(item.currentPrice)}
        </Text>
        <Text>
          <Box as='span' color='gray.500'>
            Target:{' '}
          </Box>
          {price(item.expectedPrice)}
        </Text>
        {item.expirationDate && (
          <Text>
            <Box as='span' color='gray.500'>
              Expires:{' '}
            </Box>
            {expiryLabel(item.expirationDate)}
          </Text>
        )}
        {item.isNearExpiry && (
          <Badge colorScheme='orange' fontSize='10px' alignSelf='center'>
            Near expiry
          </Badge>
        )}
      </Flex>
      {flexible.length > 0 && (
        <Flex gap='1' mt='2' wrap='wrap' align='center'>
          <Text fontSize='xs' color='gray.500'>
            Open to switch:
          </Text>
          {flexible.map((f) => (
            <Badge key={f} colorScheme='green' fontSize='10px' whiteSpace='normal'>
              {f}
            </Badge>
          ))}
        </Flex>
      )}
      {item.notes && (
        <Text fontSize='xs' color='gray.500' mt='2' noOfLines={2}>
          {item.notes}
        </Text>
      )}
    </Box>
  );
};
